import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    ShieldAlert, AlertTriangle, Users, HardDrive,
    Plug, LayoutGrid, Clock, Target,
    TrendingUp, Shield, Activity, Search,
    ChevronDown, Filter, Maximize2, MoreVertical,
    ExternalLink, CheckCircle2, X, RefreshCw,
    Server, Laptop, ShieldCheck, ArrowUpRight
} from "lucide-react";
import {
    ResponsiveContainer, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip,
    PieChart, Pie, Cell
} from "recharts";
import { backendURL } from "../config";

const KPICard = ({ title, value, icon: Icon, color, onClick, subtitle }) => (
    <div 
        onClick={onClick}
        className="bg-[#111827] border border-white/[0.06] p-4 flex flex-col items-center justify-center text-center group hover:bg-[#1a2332] hover:border-blue-500/40 transition-all cursor-pointer border-r last:border-r-0 relative overflow-hidden"
    >
        <div className="flex items-center gap-1.5 mb-1">
            <span className={`text-4xl font-light ${color} group-hover:scale-105 transition-transform`}>{typeof value === 'number' ? value.toLocaleString() : value}</span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 group-hover:text-blue-400 transition-colors uppercase tracking-widest flex items-center gap-1">
            <span>{title}</span>
            <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </span>
        {subtitle && <span className="text-[9px] text-slate-500 mt-0.5">{subtitle}</span>}
    </div>
);

const MovingFlowSankey = ({ data, onNodeClick }) => {
    if (!data || data.length === 0) return null;

    // Process nodes
    const sources = [...new Set(data.map(d => d.source))].sort();
    const targets = [...new Set(data.map(d => d.target))].sort((a, b) => {
        const valA = data.filter(d => d.target === a).reduce((sum, d) => sum + d.value, 0);
        const valB = data.filter(d => d.target === b).reduce((sum, d) => sum + d.value, 0);
        return valB - valA;
    }).slice(0, 5); // Top 5 targets

    const totalValue = data.reduce((sum, d) => sum + d.value, 0);

    const colors = {
        critical: '#ef4444',
        high: '#f97316',
        warning: '#f59e0b',
        notice: '#3b82f6',
        info: '#06b6d4',
        information: '#10b981',
        low: '#3b82f6',
        unknown: '#94a3b8'
    };

    const nodeHeight = 40;
    const gap = 12;
    const width = 650;
    const height = (Math.max(sources.length, targets.length) * (nodeHeight + gap));

    return (
        <div className="flex-1 p-2 flex flex-col relative overflow-hidden bg-[#111827]">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible">
                <defs>
                    {sources.map(s => (
                        <linearGradient key={`grad-${s}`} id={`grad-${s}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor={colors[s.toLowerCase()] || colors.unknown} stopOpacity="0.25" />
                            <stop offset="100%" stopColor={colors[s.toLowerCase()] || colors.unknown} stopOpacity="0.08" />
                        </linearGradient>
                    ))}
                </defs>

                {/* Render Links with Flows */}
                {data.filter(d => targets.includes(d.target)).map((link, i) => {
                    const sIdx = sources.indexOf(link.source);
                    const tIdx = targets.indexOf(link.target);
                    const sy = sIdx * (nodeHeight + gap) + nodeHeight / 2;
                    const ty = tIdx * (nodeHeight + gap) + nodeHeight / 2;
                    const color = colors[link.source.toLowerCase()] || colors.unknown;

                    const d = `M 120 ${sy} C 250 ${sy}, 250 ${ty}, 380 ${ty}`;

                    return (
                        <g key={`link-${i}`}>
                            <path
                                d={d}
                                fill="none"
                                stroke={color}
                                strokeWidth={Math.max(3, (link.value / totalValue) * 50)}
                                opacity="0.12"
                            />
                            <path
                                d={d}
                                fill="none"
                                stroke={color}
                                strokeWidth={2}
                                strokeDasharray="6 10"
                                style={{
                                    animation: `flow-anim ${Math.max(1, 4 - (link.value / totalValue) * 8)}s linear infinite`
                                }}
                            />
                        </g>
                    );
                })}

                {/* Render Source Nodes */}
                {sources.map((s, i) => (
                    <g 
                        key={`source-${i}`} 
                        transform={`translate(0, ${i * (nodeHeight + gap)})`}
                        onClick={() => onNodeClick && onNodeClick(s, 'source')}
                        className="cursor-pointer group"
                    >
                        <rect width="120" height={nodeHeight} fill="#f1f5f9" rx="6" className="hover:fill-blue-100 transition-colors" />
                        <text x="60" y={nodeHeight / 2} textAnchor="middle" dominantBaseline="middle" className="text-[11px] font-black uppercase italic tracking-wider fill-slate-700">
                            {s}
                        </text>
                    </g>
                ))}

                {/* Render Target Nodes */}
                {targets.map((t, i) => {
                    const targetTotal = data.filter(d => d.target === t).reduce((sum, d) => sum + d.value, 0);
                    const percentage = totalValue > 0 ? ((targetTotal / totalValue) * 100).toFixed(0) : 0;

                    return (
                        <g 
                            key={`target-${i}`} 
                            transform={`translate(380, ${i * (nodeHeight + gap)})`}
                            onClick={() => onNodeClick && onNodeClick(t, 'target')}
                            className="cursor-pointer group"
                        >
                            {/* Target Node Background */}
                            <rect width="160" height={nodeHeight} fill="#f1f5f9" rx="6" className="hover:fill-blue-50 transition-colors" />

                            {/* Device Name */}
                            <text x="10" y={nodeHeight / 2} dominantBaseline="middle" className="text-[10px] font-black uppercase italic tracking-tighter fill-slate-800">
                                {t.length > 20 ? t.substring(0, 18) + "..." : t}
                            </text>

                            {/* Value and Percentage Group */}
                            <g transform="translate(170, 0)">
                                <text x="50" y={nodeHeight / 2 - 5} textAnchor="end" className="text-[13px] font-black fill-slate-800">{targetTotal.toLocaleString()}</text>
                                <text x="50" y={nodeHeight / 2 + 8} textAnchor="end" className="text-[10px] font-bold fill-slate-400">{percentage}%</text>
                            </g>

                            {/* Activity Bits */}
                            <g transform="translate(230, 4)">
                                {[...Array(12)].map((_, bitIdx) => (
                                    <rect
                                        key={bitIdx}
                                        x={(bitIdx % 3) * 5}
                                        y={Math.floor(bitIdx / 3) * 10}
                                        width="4"
                                        height="8"
                                        fill={percentage > 20 ? "#3b82f6" : "#64748b"}
                                        className="animate-pulse"
                                        style={{ animationDelay: `${Math.random() * 2}s`, opacity: 0.7 }}
                                    />
                                ))}
                            </g>
                        </g>
                    );
                })}
            </svg>
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes flow-anim {
                    from { stroke-dashoffset: 100; }
                    to { stroke-dashoffset: 0; }
                }
            ` }} />
        </div>
    );
};

export default function SOCDashboard() {
    const navigate = useNavigate();
    const [kpis, setKpis] = useState({
        highSeverityIncidents: 0,
        outbreakAlerts: 0,
        compromisedHosts: 0,
        affectedUsers: 0,
        activeConnectors: 0,
        deviceTypes: 0
    });
    const [topIncidents, setTopIncidents] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [trendData, setTrendData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [sankeyData, setSankeyData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [timeRangeStr, setTimeRangeStr] = useState("");
    const [selectedModal, setSelectedModal] = useState(null); // null | 'kpi' | 'node' | 'connector'

    const updateTime = () => {
        const end = new Date();
        const start = new Date(Date.now() - 3600000);
        const fmt = (d) => {
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        };
        setTimeRangeStr(`${fmt(start)} - ${fmt(end)}`);
    };

    const fetchData = async () => {
        try {
            updateTime();
            const [kpiRes, topRes, connRes, trendRes, catRes, sankeyRes] = await Promise.all([
                axios.get(`${backendURL}/api/soc/dashboard/kpis`),
                axios.get(`${backendURL}/api/soc/dashboard/top-incidents`),
                axios.get(`${backendURL}/api/soc/dashboard/connectors`),
                axios.get(`${backendURL}/api/soc/dashboard/events-trend`),
                axios.get(`${backendURL}/api/soc/dashboard/incident-categories`),
                axios.get(`${backendURL}/api/soc/dashboard/sankey`)
            ]);
            setKpis(kpiRes.data);
            setTopIncidents(topRes.data || []);
            setConnectors(connRes.data || []);
            setTrendData(trendRes.data || []);
            setCategories(catRes.data || []);
            setSankeyData(sankeyRes.data || []);
        } catch (err) {
            console.error("SOC Dashboard Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 30000); // 30s refresh
        return () => clearInterval(interval);
    }, []);

    const filteredIncidents = topIncidents.filter(inc => 
        !searchQuery || 
        inc.incident_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inc.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inc.severity?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleNodeClick = (nodeName, type) => {
        if (type === 'target') {
            const n = (nodeName || '').toLowerCase();
            if (n.includes('defender') || n.includes('host device')) {
                navigate('/event-explorer?sourceType=defender');
            } else if (n.includes('threatdown') || n.includes('malwarebytes') || n.includes('affinity')) {
                navigate('/event-explorer?sourceType=threatdown');
            } else {
                navigate(`/network-traffic?device=${encodeURIComponent(nodeName)}`);
            }
        } else {
            navigate(`/event-explorer?severity=${encodeURIComponent(nodeName)}`);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full bg-[#0B1120] text-slate-100 min-h-screen">
                <div className="flex flex-col items-center gap-3">
                    <Activity className="w-8 h-8 text-blue-500 animate-spin" />
                    <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Loading Live SOC Telemetry...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-[#0B1120] font-sans text-slate-300 pb-8">
            {/* TOP BAR / PERIOD SELECTOR */}
            <div className="bg-[#111827] border-b border-white/[0.06] px-4 py-2 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-4">
                    <div 
                        onClick={fetchData}
                        className="flex items-center gap-2 text-xs font-bold text-slate-300 bg-[#1e293b] px-3 py-1.5 rounded cursor-pointer hover:bg-blue-600/20 hover:text-blue-400 transition-colors"
                    >
                        <Clock className="w-3.5 h-3.5 text-blue-400" />
                        Last 1 Hour
                        <ChevronDown className="w-3 h-3 text-slate-500" />
                    </div>
                    <span className="text-[11px] font-mono text-slate-400">
                        {timeRangeStr || "Live Telemetry Window (Past 1 Hour)"}
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={fetchData}
                        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white px-2 py-1 bg-white/[0.04] rounded hover:bg-white/[0.08] transition-colors"
                        title="Refresh live SOC metrics"
                    >
                        <RefreshCw className="w-3.5 h-3.5 text-blue-400" />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* KPI ROW */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 bg-[#111827] border-b border-white/[0.06] shadow-sm">
                <KPICard 
                    title="High Severity Incidents" 
                    value={kpis.highSeverityIncidents} 
                    color="text-rose-500" 
                    onClick={() => navigate("/incidents?severity=High")}
                />
                <KPICard 
                    title="Outbreak Alerts" 
                    value={kpis.outbreakAlerts} 
                    color="text-amber-500" 
                    onClick={() => navigate("/early-warning")}
                />
                <KPICard 
                    title="Compromised Host" 
                    value={kpis.compromisedHosts} 
                    color="text-blue-400" 
                    onClick={() => navigate("/asset-identity")}
                />
                <KPICard 
                    title="Affected Users" 
                    value={kpis.affectedUsers} 
                    color="text-purple-400" 
                    onClick={() => navigate("/asset-identity")}
                />
                <KPICard 
                    title="Active Connector" 
                    value={kpis.activeConnectors} 
                    color="text-emerald-400" 
                    onClick={() => navigate("/detection-intelligence")}
                />
                <KPICard 
                    title="Device Types" 
                    value={kpis.deviceTypes} 
                    color="text-cyan-400" 
                    onClick={() => navigate("/asset-identity")}
                />
            </div>

            {/* MAIN CONTENT GRID */}
            <div className="grid grid-cols-12 gap-2 p-2 flex-1">

                {/* EVENTS MAP (Animated Sankey) */}
                <div className="col-span-12 lg:col-span-5 bg-[#111827] border border-white/[0.06] flex flex-col overflow-hidden shadow-sm rounded-lg">
                    <div className="p-2.5 border-b border-white/[0.06] flex items-center justify-between bg-[#1a2332]/50">
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1.5">
                            <Activity className="w-3.5 h-3.5 text-blue-400" />
                            <span>Events Map (Click Node to Inspect)</span>
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">Live Multi-Vendor Telemetry Distribution</span>
                    </div>
                    <MovingFlowSankey data={sankeyData} onNodeClick={handleNodeClick} />
                </div>

                {/* INCIDENT CATEGORY */}
                <div 
                    onClick={() => navigate("/incidents")}
                    className="col-span-12 lg:col-span-3 bg-[#111827] border border-white/[0.06] flex flex-col shadow-sm rounded-lg cursor-pointer group hover:border-blue-500/30 transition-all"
                >
                    <div className="p-2.5 border-b border-white/[0.06] flex items-center justify-between bg-[#1a2332]/50">
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1.5">
                            <Target className="w-3.5 h-3.5 text-amber-400" />
                            <span>Incident Category</span>
                        </span>
                        <ArrowUpRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-blue-400 transition-colors" />
                    </div>
                    <div className="flex-1 flex items-center justify-center p-4 relative">
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={categories.length > 0 ? categories : [{ name: 'Loading...', value: kpis.highSeverityIncidents || 0 }]}
                                    innerRadius={50}
                                    outerRadius={70}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {(categories.length > 0 ? categories : [{ name: 'Unauthorized Access' }]).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'][index % 5]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', fontSize: '11px', color: '#fff', borderRadius: '8px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute flex flex-col items-center pointer-events-none">
                            <span className="text-xl font-bold text-blue-400">{(categories.reduce((a, b) => a + b.value, 0) || kpis.highSeverityIncidents).toLocaleString()}</span>
                            <span className="text-[8px] text-slate-400 font-bold uppercase tracking-tight text-center">Unauthorized<br />Access</span>
                        </div>
                    </div>
                </div>

                {/* TOP INCIDENTS */}
                <div className="col-span-12 lg:col-span-4 bg-[#111827] border border-white/[0.06] flex flex-col shadow-sm rounded-lg">
                    <div className="p-2.5 border-b border-white/[0.06] flex items-center justify-between bg-[#1a2332]/50">
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1.5">
                            <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                            <span>Top Incidents</span>
                        </span>
                        <div className="flex items-center border border-white/[0.08] rounded px-2 py-0.5 bg-[#0B1120]">
                            <Search className="w-3 h-3 text-slate-500 mr-1.5" />
                            <input 
                                type="text" 
                                placeholder="Search..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="text-[10px] outline-none border-none w-24 bg-transparent text-slate-200 placeholder:text-slate-600" 
                            />
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto max-h-[220px]">
                        <table className="w-full text-left text-[11px]">
                            <thead className="sticky top-0 bg-[#1a2332] text-slate-400 uppercase font-bold tracking-wider border-b border-white/[0.06] text-[9px]">
                                <tr>
                                    <th className="px-3 py-2 border-r border-white/[0.06]">Incidents</th>
                                    <th className="px-3 py-2 border-r border-white/[0.06]">Severity</th>
                                    <th className="px-3 py-2 border-r border-white/[0.06]">Category</th>
                                    <th className="px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {filteredIncidents.map((incident, idx) => (
                                    <tr 
                                        key={incident.incident_id || idx} 
                                        onClick={() => navigate(`/investigations/${incident.incident_id || 'QueryTel-Enterprise-Infrastructure'}`)}
                                        className="hover:bg-blue-600/10 cursor-pointer transition-colors group"
                                    >
                                        <td className="px-3 py-2 border-r border-white/[0.04] max-w-[170px] font-medium text-slate-200 group-hover:text-blue-400">
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <span className="truncate">{incident.incident_name}</span>
                                                {incident.source === "Microsoft Defender XDR" ? (
                                                    <span className="px-1.5 py-0.2 rounded text-[8px] font-bold bg-sky-500/20 text-sky-400 border border-sky-500/30 flex-shrink-0">
                                                        🛡️ XDR
                                                    </span>
                                                ) : incident.source === "ThreatDown / Malwarebytes EDR" ? (
                                                    <span className="px-1.5 py-0.2 rounded text-[8px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex-shrink-0">
                                                        ⚡ EDR
                                                    </span>
                                                ) : (
                                                    <span className="px-1.5 py-0.2 rounded text-[8px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30 flex-shrink-0">
                                                        🔥 FW
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 border-r border-white/[0.04]">
                                            <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                                                incident.severity?.toLowerCase() === 'critical' ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' :
                                                incident.severity?.toLowerCase() === 'high' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 
                                                'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                            }`}>
                                                {incident.severity}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 border-r border-white/[0.04] text-slate-400">{incident.category}</td>
                                        <td className="px-3 py-2 text-slate-500 italic">{incident.status || 'New'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div 
                        onClick={() => navigate("/incidents")}
                        className="p-2 border-t border-white/[0.04] text-[10px] font-bold text-blue-400 hover:text-blue-300 cursor-pointer text-right uppercase tracking-wider bg-[#1a2332]/30 flex items-center justify-end gap-1"
                    >
                        <span>{filteredIncidents.length} Records Found • View All Incidents</span>
                        <ChevronDown className="w-3 h-3 -rotate-90" />
                    </div>
                </div>

                {/* EVENTS AND INCIDENTS TREND */}
                <div 
                    onClick={() => navigate("/analysis")}
                    className="col-span-12 lg:col-span-8 bg-[#111827] border border-white/[0.06] flex flex-col shadow-sm rounded-lg cursor-pointer group hover:border-blue-500/30 transition-all"
                >
                    <div className="p-2.5 border-b border-white/[0.06] flex items-center justify-between bg-[#1a2332]/50">
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1.5">
                            <TrendingUp className="w-3.5 h-3.5 text-blue-400" />
                            <span>Events and Incidents Hourly Distribution</span>
                        </span>
                        <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-wider">
                            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-blue-500 rounded-sm"></div> Logs</div>
                            <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 bg-rose-500 rounded-sm"></div> Security</div>
                        </div>
                    </div>
                    <div className="flex-1 p-3 min-h-[170px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="hour" fontSize={10} stroke="#64748b" axisLine={false} tickLine={false} />
                                <YAxis fontSize={10} stroke="#64748b" axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', fontSize: '11px', color: '#fff', borderRadius: '8px' }} />
                                <Area
                                    type="monotone"
                                    dataKey="logs"
                                    stroke="#3b82f6"
                                    fill="rgba(59, 130, 246, 0.15)"
                                    strokeWidth={2}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="security"
                                    stroke="#ef4444"
                                    fill="rgba(239, 68, 68, 0.15)"
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* CONNECTOR HEALTH */}
                <div className="col-span-12 lg:col-span-4 bg-[#111827] border border-white/[0.06] flex flex-col shadow-sm rounded-lg">
                    <div className="p-2.5 border-b border-white/[0.06] flex items-center justify-between bg-[#1a2332]/50">
                        <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest flex items-center gap-1.5">
                            <Plug className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Connector Health</span>
                        </span>
                        <button 
                            onClick={() => navigate("/detection-intelligence")}
                            className="text-[10px] text-blue-400 hover:underline flex items-center gap-1"
                        >
                            <span>Manage</span>
                            <ArrowUpRight className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-3 flex-1 items-center">
                        {connectors.map((conn, idx) => (
                            <div 
                                key={idx} 
                                onClick={() => setSelectedModal(conn)}
                                className="flex flex-col items-center justify-center space-y-1.5 group cursor-pointer hover:bg-white/[0.04] p-2.5 rounded-xl transition-all border border-white/[0.04] hover:border-blue-500/30"
                            >
                                <div className={`relative p-2.5 rounded-full border-2 ${
                                    conn.status === 'online' ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-amber-500/40 bg-amber-500/10'
                                } transition-all transform group-hover:scale-110`}>
                                    <Plug className={`w-4 h-4 ${conn.status === 'online' ? 'text-emerald-400' : 'text-amber-400'}`} />
                                    {conn.status === 'online' ? (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-[#111827]"></div>
                                    ) : (
                                        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-400 rounded-full border-2 border-[#111827] flex items-center justify-center">
                                            <span className="text-[7px] text-black font-black">!</span>
                                        </div>
                                    )}
                                </div>
                                <div className="text-center">
                                    <p className="text-[10px] font-bold text-slate-200 leading-tight uppercase tracking-tight truncate max-w-[110px]">{conn.name}</p>
                                    <p className="text-[8px] text-slate-400 font-semibold uppercase mt-0.5">{conn.lastSync || "Connected"}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>

            {/* MODAL FOR CONNECTOR DETAIL */}
            {selectedModal && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-[#111827] border border-white/[0.1] rounded-2xl max-w-md w-full p-6 space-y-4 shadow-2xl">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2.5">
                                <Plug className="w-5 h-5 text-blue-400" />
                                <h3 className="text-base font-bold text-white">{selectedModal.name}</h3>
                            </div>
                            <button 
                                onClick={() => setSelectedModal(null)}
                                className="p-1 rounded-lg text-slate-500 hover:text-white hover:bg-white/[0.06] transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-3 text-xs text-slate-300">
                            <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg">
                                <span className="text-slate-400">Connector Status</span>
                                <span className="text-emerald-400 font-bold uppercase flex items-center gap-1.5">
                                    <CheckCircle2 className="w-4 h-4" />
                                    {selectedModal.status?.toUpperCase() || 'ONLINE'}
                                </span>
                            </div>
                            <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg">
                                <span className="text-slate-400">Last Telemetry Sync</span>
                                <span className="text-slate-200 font-mono">{selectedModal.lastSync || 'Just Now'}</span>
                            </div>
                            <div className="p-3 bg-white/[0.02] rounded-lg space-y-1">
                                <span className="text-slate-400 block">Integration Pipeline</span>
                                <p className="text-slate-300 leading-relaxed text-[11px]">
                                    Active REST API & Syslog collector forwarding Fortinet events into QueryTel Normalized Telemetry Engine.
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button 
                                onClick={() => setSelectedModal(null)}
                                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 text-xs font-medium rounded-lg transition-colors"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => {
                                    setSelectedModal(null);
                                    navigate("/detection-intelligence");
                                }}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-lg transition-colors"
                            >
                                Manage in Connectors
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
